import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Loader2, Info, AlertCircle } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import type { DeudaPendiente } from '@/services/aplicacionesService'

interface DeudasPendientesListProps {
  deudas: DeudaPendiente[]
  seleccionadas: Set<string>
  onToggle: (deudaId: string) => void
  onToggleTodas: () => void
  cargando: boolean
  error: string | null
  totalSeleccionadas: number
}

export function DeudasPendientesList({
  deudas,
  seleccionadas,
  onToggle,
  onToggleTodas,
  cargando,
  error,
  totalSeleccionadas,
}: DeudasPendientesListProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">
          Deudas Pendientes
        </label>
        {deudas.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onToggleTodas}
          >
            {seleccionadas.size === deudas.length
              ? 'Deseleccionar todas'
              : 'Seleccionar todas'}
          </Button>
        )}
      </div>

      {cargando ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : deudas.length === 0 ? (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            Este cliente no tiene deudas pendientes.
            Puedes registrar un pago a cuenta si lo deseas.
          </AlertDescription>
        </Alert>
      ) : (
        <div className="border rounded-lg">
          <ScrollArea className={deudas.length > 3 ? 'h-[220px]' : ''}>
            <div className="divide-y">
              {deudas.map((deuda) => (
                <label
                  key={deuda.id}
                  className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-accent/50 transition-colors ${
                    seleccionadas.has(deuda.id) ? 'bg-accent' : ''
                  }`}
                >
                  <Checkbox
                    checked={seleccionadas.has(deuda.id)}
                    onCheckedChange={() => onToggle(deuda.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">
                        {deuda.servicio_nombre || deuda.concepto}
                      </span>
                      {deuda.estado === 'parcial' && (
                        <Badge variant="outline" className="text-xs">
                          Parcial
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {deuda.mes_anio || deuda.fecha}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium text-sm">
                      {formatCurrency(deuda.saldo_pendiente)}
                    </div>
                    {deuda.monto_aplicado > 0 && (
                      <div className="text-xs text-muted-foreground">
                        de {formatCurrency(deuda.monto_original)}
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* Selection summary */}
      {seleccionadas.size > 0 && (
        <div className="flex items-center justify-between p-3 bg-accent/50 rounded-lg">
          <span className="text-sm">
            {seleccionadas.size} deuda(s) seleccionada(s)
          </span>
          <span className="font-bold">
            {formatCurrency(totalSeleccionadas)}
          </span>
        </div>
      )}
    </div>
  )
}
